// Affiliate referral integration. Two modes, both feeding the Stripe
// `client_reference_id` via getReferralId():
//
//  1. Rewardful (when VITE_REWARDFUL_API_KEY is set) — loads Rewardful's
//     script, which captures ?via= and exposes window.Rewardful.referral.
//     Rewardful owns attribution in this mode.
//
//  2. Manual / platform-free (no env key) — a generic ?ref= (or ?via=) param
//     is captured into a 60-day first-party cookie, so the program can be run
//     BY HAND (read client_reference_id off the Stripe checkout session) before
//     paying for any affiliate platform. Fully inert until someone arrives with
//     a ?ref= link; never changes checkout behavior when there's no referral.

const REWARDFUL_KEY = (
  import.meta.env as Record<string, string | undefined>
).VITE_REWARDFUL_API_KEY;

const REF_COOKIE = "solmi_ref";
const REF_MAX_AGE = 60 * 24 * 60 * 60; // 60-day cookie, in seconds

declare global {
  interface Window {
    Rewardful?: { referral?: string };
    rewardful?: (...args: unknown[]) => void;
    _rwq?: string;
  }
}

let injected = false;

function injectRewardful(): void {
  if (injected || !REWARDFUL_KEY) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  injected = true;
  // Rewardful queue stub (their documented snippet).
  (function (w: Window, r: "rewardful") {
    w._rwq = r;
    w[r] =
      w[r] ||
      function (...args: unknown[]) {
        ((w[r] as unknown as { q: unknown[] }).q =
          (w[r] as unknown as { q?: unknown[] }).q || []).push(args);
      };
  })(window, "rewardful");
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://r.wdfl.co/rw.js";
  s.setAttribute("data-rewardful", REWARDFUL_KEY);
  document.head.appendChild(s);
}

function readRefCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(/(?:^|;\s*)solmi_ref=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : undefined;
}

// Manual mode only: capture ?ref= / ?via= into a first-party cookie on landing.
// First-touch — does not overwrite an existing referral (matches Rewardful).
function captureRefParam(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    const code = p.get("ref") || p.get("via");
    if (code && !readRefCookie()) {
      const safe = encodeURIComponent(code.slice(0, 200));
      document.cookie = `${REF_COOKIE}=${safe}; max-age=${REF_MAX_AGE}; path=/; SameSite=Lax`;
    }
  } catch {
    /* no-op */
  }
}

// Runs once when this module is first imported (App imports it app-wide).
if (REWARDFUL_KEY) {
  injectRewardful();
} else {
  captureRefParam();
}

/**
 * The affiliate referral id to send to the server as `clientReferenceId`
 * (Stripe `client_reference_id`). In Rewardful mode it returns Rewardful's
 * referral id; in manual mode it returns the captured ?ref= code. Returns
 * undefined when there's no referral, leaving checkout unaffected.
 */
export function getReferralId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (REWARDFUL_KEY) return window.Rewardful?.referral || undefined;
  return readRefCookie() || undefined;
}
